import React, { useCallback, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import {
  MapEditorCatalogElement,
  hasBuiltGeometry,
  isGeometryMisconfigured,
} from '../mapEditorCatalog';
import { ModuleTreeNode, buildModuleTree } from '../mapEditorFacets';
import { useModuleTreeStyles } from './moduleTreeStyles';
import WallLayoutDialog from './WallLayoutDialog';

interface IModuleTreeProps {
  // The whole catalog. Caps are filtered out by buildModuleTree, so there is one place that
  // decides what is pickable.
  elements: MapEditorCatalogElement[];
  // Which branches are open, keyed by node. Held by the caller because this component is unmounted
  // when the Layout group is collapsed, and where an author had got to in the kit should survive
  // putting the group away and coming back — that is a glance elsewhere, not a decision to start
  // over.
  openKeys: Record<string, boolean>;
  onToggleKey: (key: string) => void;
  onPick: (elementId: string) => void;
}

// Only the values the shipped kit uses are named. Anything else is shown as the catalog authored
// it, which is the honest answer for a value this hub has never heard of — and keeps a newer
// build's modules pickable rather than hiding them behind a blank label.
const FACET_VALUE_LABELS: Record<string, { key: string; fallback: string }> = {
  'family:room': { key: 'editorMode.mapEditorFamilyRoom', fallback: 'Room' },
  'family:corridor': {
    key: 'editorMode.mapEditorFamilyCorridor',
    fallback: 'Corridor',
  },
  'direction:straight': {
    key: 'editorMode.mapEditorDirectionStraight',
    fallback: 'Straight',
  },
  'direction:left': {
    key: 'editorMode.mapEditorDirectionLeft',
    fallback: 'Turns left',
  },
  'direction:right': {
    key: 'editorMode.mapEditorDirectionRight',
    fallback: 'Turns right',
  },
  'size:S': { key: 'editorMode.mapEditorSizeS', fallback: 'Small' },
  'size:M': { key: 'editorMode.mapEditorSizeM', fallback: 'Medium' },
  'size:L': { key: 'editorMode.mapEditorSizeL', fallback: 'Large' },
};

/**
 * Names a tree row.
 *
 * A category is authored text and is shown as authored — the kit decides whether its rooms are
 * called Galleries or Rotundas, and translating it here would mean shipping a hub release to
 * rename a folder in the catalog. A facet value is one of a known handful and is translated.
 *
 * @param node - The row being labelled.
 * @param translate - i18next `t`.
 * @returns The label to render.
 */
const getNodeLabel = (node: ModuleTreeNode, translate: TFunction): string => {
  if (!node.facet) {
    return node.label;
  }

  const label = FACET_VALUE_LABELS[`${node.facet}:${node.label}`];
  if (label) {
    return translate(label.key, label.fallback);
  }

  return node.label || translate('editorMode.mapEditorFacetAny', 'Standard');
};

/**
 * The geometry warning a leaf should carry, if any.
 *
 * Only meaningful for a leaf standing for one module: a branch covers several and says nothing
 * useful about geometry until one of them is chosen, and a variant leaf answers this per card in
 * the wall-layout step instead.
 *
 * @param node - The row being rendered.
 * @returns 'unavailable' for art this build cannot load, 'placeholder' for art nobody has made
 *          yet, and undefined when there is nothing to say.
 */
const getLeafWarning = (
  node: ModuleTreeNode,
): 'unavailable' | 'placeholder' | undefined => {
  if (node.children.length || node.elements.length !== 1) {
    return undefined;
  }

  const [element] = node.elements;
  if (isGeometryMisconfigured(element)) {
    return 'unavailable';
  }

  return hasBuiltGeometry(element) ? undefined : 'placeholder';
};

/**
 * The room kit as a branch of the scene tree: category, then only the questions that tell modules
 * apart, then the modules themselves.
 *
 * A tree rather than the flat list this replaces, because the kits name their modules by geometry
 * ("Corridor 1a Short Left") and a flat list of those reads as a wall of near-identical strings.
 * A tree rather than a sequence of popups, because the whole kit stays legible at once: an author
 * can see that corridors turn two ways before committing to either.
 *
 * @param props.elements - The whole catalog; caps are filtered out internally.
 * @param props.openKeys - Which branches are open.
 * @param props.onToggleKey - Called with a branch's key when it is clicked.
 * @param props.onPick - Called with the chosen element id, once per add.
 */
const ModuleTree = ({
  elements,
  openKeys,
  onToggleKey,
  onPick,
}: IModuleTreeProps) => {
  const { t: translate } = useTranslation();
  const styles = useModuleTreeStyles();

  const tree = useMemo(() => buildModuleTree(elements), [elements]);

  // Which module's variants the wall-layout step is asking about. Null while it is closed. Local
  // rather than lifted: a half-answered question is not a place to come back to, and the group
  // cannot be collapsed while a modal is over it anyway.
  const [variantNode, setVariantNode] = useState<ModuleTreeNode | null>(null);

  // A leaf covering one module adds it. A leaf covering several is a set of wall-layout variants —
  // same room, different walls — and asks which one before adding.
  const handleLeafClick = useCallback(
    (node: ModuleTreeNode) => {
      if (node.elements.length === 1) {
        onPick(node.elements[0].id);
        return;
      }
      setVariantNode(node);
    },
    [onPick],
  );

  const renderNode = (node: ModuleTreeNode, depth: number) => {
    const isBranch = node.children.length > 0;
    // A leaf covering several modules opens the wall-layout step instead of placing one outright.
    // It says how many it is choosing between, so it does not look like the row next to it that
    // adds without asking.
    const isVariantLeaf = !isBranch && node.elements.length > 1;
    const isOpen = !!openKeys[node.key];
    const warning = getLeafWarning(node);
    const label = getNodeLabel(node, translate);

    const row = (
      <Box
        sx={[
          styles.row,
          !isBranch && styles.rowLeaf,
          warning === 'placeholder' && styles.rowUnbuilt,
          // Indent by depth. The first level sits flush with the tree's other rows.
          { paddingLeft: `${8 + depth * 14}px` },
        ]}
        onClick={() =>
          isBranch ? onToggleKey(node.key) : handleLeafClick(node)
        }
        data-testid={`module-tree-${isBranch ? 'branch' : 'leaf'}-${node.key}`}
      >
        {isBranch ? (
          <ChevronRightOutlinedIcon
            sx={[styles.rowChevron, isOpen && styles.rowChevronOpen]}
          />
        ) : (
          <Box sx={styles.rowLeafSpacer} />
        )}

        {!isBranch && <MeetingRoomOutlinedIcon sx={styles.rowIcon} />}

        <Typography sx={styles.rowLabel}>{label}</Typography>

        {(isBranch || isVariantLeaf) && (
          <Typography sx={styles.rowCount}>{node.elements.length}</Typography>
        )}
        {!isBranch && <AddCircleOutlineOutlinedIcon sx={styles.rowAddIcon} />}

        {!!warning && (
          <WarningAmberOutlinedIcon
            sx={[
              styles.rowWarning,
              warning === 'unavailable' && styles.rowWarningSevere,
            ]}
            data-testid={`module-tree-warning-${node.elements[0].id}`}
          />
        )}
      </Box>
    );

    return (
      <React.Fragment key={node.key}>
        {warning ? (
          <Tooltip
            title={
              warning === 'unavailable'
                ? translate(
                    'editorMode.mapEditorGeometryUnavailable',
                    'This element names a room this build cannot load — Unreal falls back to a grey placeholder',
                  )
                : translate(
                    'editorMode.mapEditorNoGeometry',
                    'This element has no room built yet — Unreal builds a grey placeholder sized to it',
                  )
            }
            placement="right"
            enterDelay={350}
          >
            {row}
          </Tooltip>
        ) : (
          row
        )}
        {isBranch &&
          isOpen &&
          node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <Typography sx={styles.sectionLabel}>
        {translate('editorMode.mapEditorAddRoom', 'Add room')}
      </Typography>

      {tree.length === 0 ? (
        <Typography sx={styles.empty} data-testid="module-tree-empty">
          {translate(
            'editorMode.mapEditorFacetNoModules',
            'This build ships no modules to pick from.',
          )}
        </Typography>
      ) : (
        tree.map((node) => renderNode(node, 0))
      )}

      <WallLayoutDialog
        open={!!variantNode}
        variants={variantNode?.elements ?? []}
        moduleLabel={variantNode ? getNodeLabel(variantNode, translate) : ''}
        onClose={() => setVariantNode(null)}
        onPick={(elementId) => {
          onPick(elementId);
          setVariantNode(null);
        }}
      />
    </>
  );
};

export default ModuleTree;
